import type {
  CardIssuerClient,
  CardViewerField,
  RenderCardTokenOptions,
} from "utils/cards/cardViewer";

const FRAME_FIELDS: CardViewerField[] = ["cardNumber", "cvv", "expirationDate"];
// The one frame handed the single-use secret; it shares the details with the others.
const REVEALING_FIELD: CardViewerField = "cardNumber";
const FRAME_SOURCE = "outpave-card-frame";
const FRAME_TIMEOUT_MS = 20_000;

interface FrameMessage {
  source?: string;
  type?: "loaded" | "listening" | "ready" | "copied" | "error";
  width?: number;
  height?: number;
}

export const corpayCardClient: CardIssuerClient = {
  features: { copyButton: false },
  render: renderCorpayCard,
};

/** The card frame page on the API's origin, which shows one field of a card. */
export const getCardFrameUrl = (
  apiBaseUrl = process.env.REACT_APP_API_BASE_URL,
) => new URL("card-frame", `${String(apiBaseUrl).replace(/\/+$/, "")}/`);

async function renderCorpayCard({
  cardToken: { secret },
  selectors,
  style,
  onCopy,
  onLateError,
  isCurrent = () => true,
  labels,
}: RenderCardTokenOptions): Promise<boolean> {
  const frameUrl = getCardFrameUrl();
  const frames = mountFrames(frameUrl, selectors, labels);
  await runHandshake({
    frames,
    frameOrigin: frameUrl.origin,
    secret,
    style: style.base ?? {},
    onCopy,
    onLateError,
  });
  return isCurrent();
}

function mountFrames(
  frameUrl: URL,
  selectors: Record<CardViewerField, string>,
  labels: Record<CardViewerField, string>,
): Record<CardViewerField, HTMLIFrameElement> {
  const entries = FRAME_FIELDS.map((field) => {
    const slot = document.querySelector(selectors[field]);
    if (!slot) throw new Error(`The card's ${field} slot is not on the page.`);
    const url = new URL(frameUrl);
    url.searchParams.set("field", field);
    url.searchParams.set("parent", window.location.origin);
    const frame = document.createElement("iframe");
    frame.src = url.href;
    frame.title = labels[field];
    frame.allow = "clipboard-write";
    frame.style.border = "0";
    // Matches the frame document's scheme so a dark page does not paint the frame opaque.
    frame.style.colorScheme = "normal";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.visibility = "hidden";
    slot.appendChild(frame);
    return [field, frame] as const;
  });
  return Object.fromEntries(entries) as Record<
    CardViewerField,
    HTMLIFrameElement
  >;
}

/**
 * Resolves once every frame shows its field and rejects when a frame fails first. The secret goes
 * to its frame only after the others listen on the channel it shares the details over.
 */
const runHandshake = ({
  frames,
  frameOrigin,
  secret,
  style,
  onCopy,
  onLateError,
}: {
  frames: Record<CardViewerField, HTMLIFrameElement>;
  frameOrigin: string;
  secret: string;
  style: object;
  onCopy: (field: CardViewerField) => void;
  onLateError: (error: unknown) => void;
}) =>
  new Promise<void>((resolve, reject) => {
    const channel = crypto.randomUUID();
    const loaded = new Set<CardViewerField>();
    const listening = new Set<CardViewerField>();
    const ready = new Set<CardViewerField>();
    let settled = false;

    const send = (field: CardViewerField, message: object) =>
      frames[field].contentWindow?.postMessage(
        { type: "init", channel, style, ...message },
        frameOrigin,
      );
    const settle = (error?: unknown) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      if (error) reject(error);
      else resolve();
    };
    const timeout = window.setTimeout(
      () => settle(new Error("The card frames did not answer in time.")),
      FRAME_TIMEOUT_MS,
    );

    const onMessage = (event: MessageEvent<FrameMessage>) => {
      if (!frames[REVEALING_FIELD].isConnected) {
        window.removeEventListener("message", onMessage);
        settle(new Error("The card was closed."));
        return;
      }
      if (event.origin !== frameOrigin || event.data?.source !== FRAME_SOURCE)
        return;
      const field = FRAME_FIELDS.find(
        (candidate) => frames[candidate].contentWindow === event.source,
      );
      if (!field) return;

      switch (event.data.type) {
        case "loaded":
          loaded.add(field);
          if (loaded.size === FRAME_FIELDS.length) {
            FRAME_FIELDS.filter((other) => other !== REVEALING_FIELD).forEach(
              (other) => send(other, {}),
            );
          }
          break;
        case "listening":
          listening.add(field);
          if (listening.size === FRAME_FIELDS.length - 1) {
            send(REVEALING_FIELD, { secret });
          }
          break;
        case "ready":
          showFrame(frames[field], event.data);
          ready.add(field);
          if (ready.size === FRAME_FIELDS.length) settle();
          break;
        case "copied":
          onCopy(field);
          break;
        case "error": {
          const error = new Error(`The card's ${field} could not be shown.`);
          if (settled) onLateError(error);
          else settle(error);
          break;
        }
      }
    };
    window.addEventListener("message", onMessage);
  });

function showFrame(frame: HTMLIFrameElement, { width, height }: FrameMessage) {
  if (width) frame.style.width = `${width}px`;
  if (height) frame.style.height = `${height}px`;
  frame.style.visibility = "visible";
}
